import { useState, useEffect } from "react";
import { FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDossierStore } from "../../store/dossierStore";
import { hasCabinet } from "../../lib/dossiers";
import {
  fetchTransactionsPourExport,
  buildExportCsv,
  downloadBlob,
  type ExportDateFormat,
} from "../../lib/export-operations";

function getDefaultDateRange(): { dateDebut: string; dateFin: string } {
  const today = new Date();
  const debut = new Date(today.getFullYear(), 0, 1);
  return {
    dateDebut: debut.toISOString().slice(0, 10),
    dateFin: today.toISOString().slice(0, 10),
  };
}

export default function ExportOperations() {
  const navigate = useNavigate();
  const { activeDossier } = useDossierStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nbTransactions, setNbTransactions] = useState<number | null>(null);
  const [dateDebut, setDateDebut] = useState(() => getDefaultDateRange().dateDebut);
  const [dateFin, setDateFin] = useState(() => getDefaultDateRange().dateFin);
  const [dateFormat, setDateFormat] = useState<ExportDateFormat>("sage");

  useEffect(() => {
    hasCabinet().then((ok) => {
      if (!ok) navigate("/dashboard", { replace: true });
    });
  }, [navigate]);

  const range = { dateDebut, dateFin };
  const rangeInvalide = dateDebut > dateFin;

  useEffect(() => {
    if (!activeDossier?.id) {
      setNbTransactions(null);
      setLoading(false);
      return;
    }
    if (rangeInvalide) {
      setNbTransactions(0);
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    fetchTransactionsPourExport(activeDossier.id, range)
      .then((data) => setNbTransactions(data.length))
      .catch((e) => setError(e instanceof Error ? e.message : "Erreur"))
      .finally(() => setLoading(false));
  }, [activeDossier?.id, dateDebut, dateFin, rangeInvalide]);

  const handleExportCsv = async () => {
    if (!activeDossier) return;
    setLoading(true);
    setError(null);
    try {
      const transactions = await fetchTransactionsPourExport(activeDossier.id, range);
      const csv = buildExportCsv(transactions, { dateFormat });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const nomDossier = activeDossier.nom.replace(/[^a-zA-Z0-9-_]/g, "_");
      downloadBlob(blob, `woahda_${nomDossier}_${dateDebut}_${dateFin}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur export");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px" }}>
      <div
        style={{
          background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(96,165,250,0.05))",
          border: "1px solid rgba(59,130,246,0.2)",
          borderRadius: "16px",
          padding: "24px 32px",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 800, color: "#1A0A00", marginBottom: "4px" }}>
          Exporter les opérations
        </h1>
        <p style={{ fontSize: "0.9rem", color: "#5A4A3A", lineHeight: 1.5 }}>
          Exportez les opérations affectées au format CSV pour les importer dans votre ERP (Sage,
          etc.).
          {activeDossier && (
            <span style={{ fontWeight: 600, marginLeft: "4px" }}>— {activeDossier.nom}</span>
          )}
        </p>
      </div>

      {error && (
        <div
          style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "20px",
            color: "#EF4444",
          }}
        >
          ⚠ {error}
        </div>
      )}

      {!activeDossier ? (
        <div
          style={{
            background: "white",
            border: "1px solid #EAE0D5",
            borderRadius: "16px",
            padding: "48px 24px",
            textAlign: "center",
            color: "#9A8878",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px", color: "#9A8878" }}><FolderOpen size={40} strokeWidth={1.5} /></div>
          <div style={{ fontWeight: 600, marginBottom: "8px" }}>Sélectionnez un dossier</div>
          <div style={{ fontSize: "0.9rem" }}>
            Choisissez un dossier dans le sélecteur en haut à droite pour exporter ses opérations.
          </div>
        </div>
      ) : (
        <div
          style={{
            background: "white",
            border: "1px solid #EAE0D5",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          {loading && nbTransactions === null ? (
            <div style={{ textAlign: "center", color: "#9A8878", padding: "24px" }}>
              Chargement…
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "20px",
                  marginBottom: "24px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <label
                    htmlFor="date-debut"
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#9A8878",
                      marginBottom: "4px",
                      fontWeight: 500,
                    }}
                  >
                    Du
                  </label>
                  <input
                    id="date-debut"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    style={{
                      padding: "10px 14px",
                      border: "1px solid #EAE0D5",
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      fontFamily: "'Sora', sans-serif",
                      background: "#FAF6F1",
                      color: "#1A0A00",
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="date-fin"
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#9A8878",
                      marginBottom: "4px",
                      fontWeight: 500,
                    }}
                  >
                    Au
                  </label>
                  <input
                    id="date-fin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    style={{
                      padding: "10px 14px",
                      border: "1px solid #EAE0D5",
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      fontFamily: "'Sora', sans-serif",
                      background: "#FAF6F1",
                      color: "#1A0A00",
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="date-format"
                    style={{
                      display: "block",
                      fontSize: "0.75rem",
                      color: "#9A8878",
                      marginBottom: "4px",
                      fontWeight: 500,
                    }}
                  >
                    Format des dates
                  </label>
                  <select
                    id="date-format"
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value as ExportDateFormat)}
                    style={{
                      padding: "10px 14px",
                      border: "1px solid #EAE0D5",
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      fontFamily: "'Sora', sans-serif",
                      background: "#FAF6F1",
                      color: "#1A0A00",
                      minWidth: 180,
                    }}
                  >
                    <option value="sage">JJ/MM/AAAA (Sage France)</option>
                    <option value="iso">AAAA-MM-JJ (ISO / générique)</option>
                  </select>
                </div>
                {rangeInvalide && (
                  <div style={{ fontSize: "0.85rem", color: "#D97706", alignSelf: "center" }}>
                    La date de début doit être avant la date de fin.
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "24px",
                  marginBottom: "24px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    background: "#FAF6F1",
                    border: "1px solid #EAE0D5",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    flex: "1",
                    minWidth: 200,
                  }}
                >
                  <div style={{ fontSize: "0.75rem", color: "#9A8878", marginBottom: "4px" }}>
                    Opérations affectées (période sélectionnée)
                  </div>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1A0A00" }}>
                    {nbTransactions ?? 0}
                  </div>
                </div>
                <button
                  onClick={handleExportCsv}
                  disabled={loading || rangeInvalide || (nbTransactions ?? 0) === 0}
                  style={{
                    padding: "14px 28px",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      (nbTransactions ?? 0) > 0
                        ? "linear-gradient(135deg, #3B82F6, #60A5FA)"
                        : "#E5E7EB",
                    color: (nbTransactions ?? 0) > 0 ? "white" : "#9CA3AF",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    cursor: loading || (nbTransactions ?? 0) === 0 ? "not-allowed" : "pointer",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  {loading ? "Export…" : "📤 Télécharger CSV"}
                </button>
              </div>
              {(nbTransactions ?? 0) === 0 && (
                <p style={{ fontSize: "0.85rem", color: "#9A8878" }}>
                  Aucune opération affectée à exporter pour ce dossier. Les clients doivent d'abord
                  affecter leurs transactions.
                </p>
              )}
              {(nbTransactions ?? 0) > 0 && (
                <div style={{ fontSize: "0.8rem", color: "#9A8878", lineHeight: 1.6 }}>
                  <p style={{ marginBottom: "8px", fontWeight: 600, color: "#5A4A3A" }}>
                    Format compatible import ERP (Sage, Banana Comptabilité, etc.)
                  </p>
                  <p>
                    CSV UTF-8, séparateur point-virgule. Colonnes : <strong>N° pièce</strong> (regroupe
                    les lignes d&apos;une même écriture), Date, Journal, Compte, Libellé compte, Libellé,
                    Débit, Crédit, Devise (XOF). Décimales au point.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
